/**
 * NextStopBar — de volgende stop, altijd in beeld.
 *
 * Mintbalk (kleur "nu") direct onder de routekop, sticky. ETA mono groot,
 * "Volgende: {straat}", afstand/minuten. Tik = scroll naar die kaart.
 * De pagina bepaalt `visible` (IntersectionObserver op de kaart zelf): staat
 * de actuele kaart in beeld, dan verbergt de balk zich.
 */

import clsx from 'clsx';
import { typeLabel } from './WbLabelStrip';

export interface NextStopBarStop {
  nummer: string | number;
  naam: string;
  straat: string;
  eta?: string | null;
  afstand?: string | null;
  minuten?: string | null;
  type?: string;
}

export interface NextStopBarProps {
  stop: NextStopBarStop | null;
  /** DOM-id van de stopkaart waar naartoe gescrold wordt. */
  targetId: string;
  visible: boolean;
  /** Afstand vanaf de bovenkant waar de balk blijft plakken (routekop). */
  top?: number | string;
  className?: string;
}

export function NextStopBar({ stop, targetId, visible, top = 0, className }: NextStopBarProps) {
  if (!stop || !visible) return null;

  const meta = [stop.afstand, stop.minuten].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={() => document.getElementById(targetId)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
      className={clsx(
        'wb-focus sticky z-20 w-full min-h-[56px] bg-wb-mint text-wb-mintink border-b-wb border-wb-mintink flex items-center gap-3 px-3 text-left',
        className
      )}
      style={{ top }}
      aria-label={`Volgende stop ${stop.nummer}: ${stop.naam}, ${stop.straat}`}
    >
      <span className="font-mono text-2xl font-semibold tabular-nums shrink-0">{stop.eta ?? `#${stop.nummer}`}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[11px] font-bold uppercase tracking-wide leading-tight">
          Volgende · {typeLabel(stop.type)}
        </span>
        <span className="block font-wb-heading font-bold text-base leading-tight truncate">{stop.straat || stop.naam}</span>
      </span>
      {meta && <span className="font-mono text-sm shrink-0">{meta}</span>}
      <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}
